import { useState } from "react";

import { Button, Grid, Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  resetUserRetryCount,
} from "../../Api/Doctor/Doctor.Api";
import { getAllPatients } from "../../Api/Patient/Patient.Api";
import SearchInput from "../../Components/atoms/Input/SearchInput";
import {
  Heading1,
  Heading3,
} from "../../Components/atoms/Typography/Heading/Heading";
import { BasicGrid } from "../../Components/organisms/BasicGrid/BasicGrid";
import ConfirmDialog from "../../Components/organisms/ConfirmDialog/ConfirmDialog";
import { permissionValidator, useAuth } from "../../Store/AuthContext";
import useApiData from "../../Utils/Hooks/useApiData";
import useLoadingState from "../../Utils/Hooks/useLodingState";
import useOverlayState from "../../Utils/Hooks/useOverlayState";
import { getColumns } from "./GridColumns";

export const PatientsGrid = () => {
  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  const { state } = useAuth();
  const [searchText, setSearchText] = useState("");

  const { t } = useTranslation();
  const [pageData, setPageData] = useState({
    currentPage: 1,
    pageCount: 0,
    pageSize: 10,
    totalPages: 0,
    totalRecordsCount: 0,
  });

  const apiCall = async () => {
    let langCode = new String(state?.lang?.code);
    const response = await getAllPatients({
      page: pageData.currentPage,
      size: pageData.pageSize,
      searchText: searchText,
      language: langCode.toUpperCase(),
    });
    if (response.type == 0) {
      return response;
    } else {
      throw new Error(response.message || "Failed to fetch doctor data");
    }
  };

  const { isLoading, data, error, refetch } = useApiData({
    apiCall: apiCall,
    dependency: [pageData.currentPage, pageData.pageSize, searchText],
    pagination: {
      isServerSidePagination: true,
      setApiData: (totalRecordsCount, pageSize, pageNumber, pagesCount) => {
        if (
          pageData.currentPage !== pageNumber ||
          pageSize !== pageData.pageSize
        ) {
          setPageData({
            ...pageData,
            currentPage:
              pageData.currentPage !== pageNumber
                ? pageNumber
                : pageData.currentPage,
            pageSize:
              pageSize !== pageData.pageSize ? pageSize : pageData.pageSize,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        } else {
          setPageData({
            ...pageData,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        }
      },
    },
  });

  const clickHandler = async (e, data) => {
    e !== 1 && startLoading({ btnName: "load" });
    if (e === 5) {
      await resetUserRetryCount(data?.email).then((response) => {
        openOverlay({
          mode: Number(response.type),
          messages: {
            title: t("glossary:auth.reset-otp-count.reset-otp-count"),
            description:
              response.type == 0
                ? t("glossary:auth.reset-otp-count.succeed-message")
                : t("glossary:auth.reset-otp-count.unsucceed-message"),
          },
        });
        response.type == 0 && refetch();
      });
    }

    e !== 1 && stopLoading();
  };

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack direction={"row"} justifyContent={"space-between"} mb={2.8}>
            <Heading1 text={t("glossary:patient.title")} />
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <SearchInput
                width={"319.42px"}
                searchValue={searchText}
                placeholder={t("common:placeholder.patientid-name-email-phonenumber")}
                getSearchText={(value) => {
                  setSearchText(value);
                  setPageData({ ...pageData, currentPage: 1 });
                }}
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid width="100%" item>
          <Paper
            sx={{
              padding: "20px 24px",
              boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
              position: "relative",
              height: "100%",
            }}
          >
            <BasicGrid
              // isError={error}
              isLoading={isLoading || LoadingState.visible}
              rowSelect={false}
              data={data}
              pageData={pageData}
              columnArray={getColumns(t, clickHandler)}
              allowSorting
              allowPaging
              field={["EmployeeID", "LastName"]}
              paginationClick={(pageNumber) => {
                setPageData({ ...pageData, currentPage: pageNumber });
              }}
              pageSizeChange={(selectedPageSize) => {
                setPageData({ ...pageData, pageSize: selectedPageSize });
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {OverlayState.visible &&
        !OverlayState.OverlayName &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail" || OverlayState.mode === 3}
            title={OverlayState.messages?.title}
            description={OverlayState.messages?.description}
            open={OverlayState?.visible}
            close={() => {
              closeOverlay();
            }}
            onOkClick={() => {
              closeOverlay();
            }}
          />
        )}
    </>
  );
};
